<template>
  <div class="landing-page-editor space-y-4">
    <div class="card p-4 space-y-3">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <label class="flex flex-col text-sm grow min-w-0">
          <span class="font-medium" style="color: var(--text-secondary);">Page name</span>
          <input
            v-model="title"
            class="form-input mt-1"
            type="text"
            maxlength="255"
            data-field="page-title"
            :disabled="readOnly">
        </label>
        <div class="flex flex-wrap gap-2 items-end">
          <button
            v-if="!readOnly"
            type="button"
            class="btn btn-primary"
            data-action="save"
            :disabled="saving || !dirty"
            @click="save()">
            {{ saving ? 'Saving…' : 'Save draft' }}
          </button>
          <button
            v-if="requestPreview"
            type="button"
            class="btn btn-secondary"
            data-action="preview"
            :disabled="previewState === 'loading'"
            @click="preview">
            {{ previewState === 'loading' ? 'Preparing preview…' : 'Preview' }}
          </button>
          <button
            v-if="!readOnly"
            type="button"
            class="btn btn-secondary"
            data-action="open-publish"
            :disabled="dirty"
            :title="dirty ? 'Save your changes before publishing' : undefined"
            @click="$emit('publish')">
            Publish…
          </button>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2 text-sm" style="color: var(--text-secondary);">
        <span>Status: <strong style="color: var(--text-primary);">{{ statusLabel }}</strong></span>
        <span aria-hidden="true">·</span>
        <span class="break-all">Address: {{ page.path }}</span>
        <button
          v-if="!readOnly && changePath && !editingPath"
          type="button"
          class="underline"
          data-action="edit-path"
          @click="startPathEdit">
          Change address
        </button>
        <span v-if="page.canonical_url" class="break-all">
          · Live at <a :href="page.canonical_url" target="_blank" rel="noopener" class="underline">{{ page.canonical_url }}</a>
        </span>
      </div>
      <form v-if="editingPath" class="flex flex-wrap items-end gap-2" @submit.prevent="submitPath">
        <label class="flex flex-col text-sm">
          <span class="font-medium" style="color: var(--text-secondary);">New address (path)</span>
          <input v-model="pathDraft" class="form-input mt-1" type="text" data-field="path" placeholder="/emergency-plumbing">
        </label>
        <button type="submit" class="btn btn-primary btn-sm" data-action="change-path" :disabled="pathSaving">
          {{ pathSaving ? 'Changing…' : 'Change address' }}
        </button>
        <button type="button" class="btn btn-secondary btn-sm" @click="editingPath = false">
          Cancel
        </button>
        <p class="w-full text-xs" style="color: var(--text-secondary);">
          The old address keeps working while it is still published. Save your draft first.
        </p>
      </form>

      <div
        v-if="readOnly"
        class="rounded-lg border p-3 text-sm"
        style="border-color: var(--border-primary, #e5e7eb); color: var(--text-secondary);"
        role="note">
        {{ readOnlyReason }}
      </div>
      <div
        v-if="conflict"
        class="rounded-lg border border-amber-400 p-3 text-sm space-y-2"
        style="color: var(--text-primary);"
        role="alert"
        data-test="conflict">
        <p class="font-medium">
          This page changed while you were editing.
        </p>
        <p style="color: var(--text-secondary);">
          Your edits are still here and have not been saved. Load the latest version (your edits are discarded),
          or replace the latest version with your edits.
        </p>
        <div class="flex flex-wrap gap-2">
          <button type="button" class="btn btn-secondary btn-sm" data-action="reload-latest" @click="$emit('reload')">
            Load the latest version
          </button>
          <button type="button" class="btn btn-primary btn-sm" data-action="overwrite" :disabled="saving" @click="overwrite">
            Replace it with my edits
          </button>
        </div>
      </div>
      <p role="status" aria-live="polite" class="text-sm" :class="messageClass" data-test="editor-status">
        {{ message ? message.text : '' }}
      </p>
    </div>

    <div class="grid gap-4 xl:grid-cols-2">
      <div class="space-y-4 min-w-0">
        <SectionEditor
          v-for="(section, index) in document.sections"
          :key="section.id"
          :section="section"
          :index="index"
          :definition="definitionFor(section.type)"
          :editor="editorFor(section.type)"
          :errors="sectionErrors(index)"
          :read-only="readOnly"
          :can-move-up="canMove(index, -1)"
          :can-move-down="canMove(index, 1)"
          :can-remove="canRemove(section)"
          :upload-asset="uploadAsset"
          :assets="assets"
          @update="updateSection(index, $event)"
          @move="moveSection(index, $event)"
          @remove="removeSection(index)"
          @error="showError" />

        <div v-if="!readOnly && addableTypes.length" class="card p-4 flex flex-wrap items-end gap-2">
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Add a section</span>
            <select v-model="newSectionType" class="form-select mt-1" data-field="add-section">
              <option v-for="type in addableTypes" :key="type" :value="type">
                {{ SECTION_TITLES[type] || type }}
              </option>
            </select>
          </label>
          <button type="button" class="btn btn-secondary" data-action="add-section" @click="addSection">
            Add section
          </button>
        </div>

        <section v-for="(form, formIndex) in document.forms" :key="form.instance_id" class="card p-4 space-y-3" data-test="form-editor">
          <h3 class="font-semibold" style="color: var(--text-primary);">
            Form fields
          </h3>
          <div
            v-for="(field, fieldIndex) in form.fields"
            :key="`${form.instance_id}-${field.key}`"
            class="grid gap-2 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
            <label class="flex flex-col text-sm min-w-0">
              <span class="font-medium" style="color: var(--text-secondary);">Label</span>
              <input
                class="form-input mt-1"
                type="text"
                maxlength="160"
                :data-field="`forms.${formIndex}.fields.${fieldIndex}.label`"
                :value="field.label"
                :disabled="readOnly || field.key === 'consent'"
                @input="setFormField(formIndex, fieldIndex, { label: $event.target.value })">
            </label>
            <label class="flex flex-col text-sm">
              <span class="font-medium" style="color: var(--text-secondary);">Type</span>
              <select
                class="form-select mt-1"
                :data-field="`forms.${formIndex}.fields.${fieldIndex}.type`"
                :value="field.type"
                :disabled="readOnly || field.key === 'consent'"
                @change="setFormField(formIndex, fieldIndex, { type: $event.target.value })">
                <option v-for="type in FIELD_TYPES" :key="type" :value="type">
                  {{ FIELD_TYPE_LABELS[type] }}
                </option>
              </select>
            </label>
            <label class="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                :checked="field.required"
                :disabled="readOnly || field.key === 'consent'"
                :data-field="`forms.${formIndex}.fields.${fieldIndex}.required`"
                @change="setFormField(formIndex, fieldIndex, { required: $event.target.checked })">
              Required
            </label>
            <button
              v-if="!readOnly && field.key !== 'consent' && form.fields.length > 1"
              type="button"
              class="btn btn-secondary btn-sm"
              data-action="remove-form-field"
              @click="removeFormField(formIndex, fieldIndex)">
              Remove
            </button>
          </div>
          <button
            v-if="!readOnly && form.fields.length < 10"
            type="button"
            class="btn btn-secondary btn-sm"
            data-action="add-form-field"
            @click="addFormField(formIndex)">
            Add a field
          </button>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Consent statement</span>
            <textarea
              class="form-input mt-1"
              rows="2"
              maxlength="1000"
              :data-field="`forms.${formIndex}.consent_text`"
              :value="form.consent_text || ''"
              :disabled="readOnly"
              @input="setConsent(formIndex, $event.target.value)" />
            <span class="text-xs mt-1" style="color: var(--text-secondary);">Visitors must tick this before sending.</span>
          </label>
          <p v-for="entry in formErrors(formIndex)" :key="entry" class="text-xs text-red-700" role="alert">
            {{ entry }}
          </p>
        </section>

        <section class="card p-4 space-y-3" data-test="brand-editor">
          <h3 class="font-semibold" style="color: var(--text-primary);">
            Brand
          </h3>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Business name shown on the page</span>
            <input
              class="form-input mt-1"
              type="text"
              maxlength="160"
              data-field="brand.name"
              :value="document.brand.name || ''"
              :disabled="readOnly"
              @input="setBrand({ name: $event.target.value })">
          </label>
          <div class="grid gap-3 sm:grid-cols-2">
            <label v-for="color in BRAND_COLORS" :key="color.key" class="flex flex-col text-sm">
              <span class="font-medium" style="color: var(--text-secondary);">{{ color.label }}</span>
              <span class="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  class="h-9 w-12 rounded border"
                  :aria-label="color.pick"
                  :value="validColor(document.brand[color.key]) ? document.brand[color.key] : color.fallback"
                  :disabled="readOnly"
                  @input="setBrand({ [color.key]: $event.target.value })">
                <input
                  class="form-input grow"
                  type="text"
                  maxlength="7"
                  :placeholder="color.placeholder"
                  :data-field="`brand.${color.key}`"
                  :value="document.brand[color.key] || ''"
                  :disabled="readOnly"
                  @input="setBrand({ [color.key]: $event.target.value })">
              </span>
              <span v-if="color.hint" class="text-xs mt-1" style="color: var(--text-secondary);">{{ color.hint }}</span>
            </label>
          </div>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Logo</span>
            <select
              class="form-select mt-1"
              data-field="brand.logo_asset_id"
              :value="document.brand.logo_asset_id || ''"
              :disabled="readOnly"
              @change="setBrand({ logo_asset_id: $event.target.value || null })">
              <option value="">
                No logo
              </option>
              <option v-if="document.brand.logo_asset_id && !assets.some((asset) => asset.id === document.brand.logo_asset_id)" :value="document.brand.logo_asset_id">
                Current logo
              </option>
              <option v-for="asset in assets" :key="asset.id" :value="asset.id">
                {{ assetLabel(asset) }}
              </option>
            </select>
            <span class="text-xs mt-1" style="color: var(--text-secondary);">
              {{ assetsState === 'failed' ? 'The uploaded images could not be listed. Try reopening the page.' : 'Choose from images uploaded to this page.' }}
            </span>
          </label>
          <div v-if="!readOnly && uploadAsset" class="text-sm">
            <label class="btn btn-secondary btn-sm focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
              {{ logoUploading ? 'Uploading…' : 'Upload a logo' }}
              <input
                type="file"
                class="sr-only"
                accept="image/jpeg,image/png,image/webp,image/avif"
                data-field="brand.logo-upload"
                :disabled="logoUploading"
                @change="uploadLogo">
            </label>
          </div>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Font</span>
            <select
              class="form-select mt-1"
              data-field="brand.font_family"
              :value="document.brand.font_family || 'system'"
              :disabled="readOnly"
              @change="setBrand({ font_family: $event.target.value })">
              <option value="system">System</option>
              <option value="sans">Sans serif</option>
              <option value="serif">Serif</option>
            </select>
          </label>
          <p v-for="entry in errorsUnder('/brand')" :key="entry" class="text-xs text-red-700" role="alert">
            {{ entry }}
          </p>
        </section>

        <section class="card p-4 space-y-3" data-test="seo-editor">
          <h3 class="font-semibold" style="color: var(--text-primary);">
            Search listing
          </h3>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Title</span>
            <input
              class="form-input mt-1"
              type="text"
              maxlength="70"
              data-field="seo.title"
              :value="document.seo.title || ''"
              :disabled="readOnly"
              @input="document.seo = { ...document.seo, title: $event.target.value }">
          </label>
          <label class="flex flex-col text-sm">
            <span class="font-medium" style="color: var(--text-secondary);">Description</span>
            <textarea
              class="form-input mt-1"
              rows="2"
              maxlength="160"
              data-field="seo.description"
              :value="document.seo.description || ''"
              :disabled="readOnly"
              @input="document.seo = { ...document.seo, description: $event.target.value }" />
          </label>
        </section>
      </div>

      <aside class="space-y-2 min-w-0 xl:sticky xl:top-28 xl:self-start" aria-label="Preview">
        <div class="card p-4 space-y-3">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h3 class="font-semibold" style="color: var(--text-primary);">
              Preview
            </h3>
            <div class="flex gap-1" role="group" aria-label="Preview width">
              <button
                type="button"
                class="btn btn-sm"
                :class="viewport === 'desktop' ? 'btn-primary' : 'btn-secondary'"
                data-action="preview-desktop"
                :aria-pressed="viewport === 'desktop'"
                @click="viewport = 'desktop'">
                Desktop
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="viewport === 'mobile' ? 'btn-primary' : 'btn-secondary'"
                data-action="preview-mobile"
                :aria-pressed="viewport === 'mobile'"
                @click="viewport = 'mobile'">
                Mobile
              </button>
            </div>
          </div>
          <div
            data-test="preview-viewport"
            :data-viewport="viewport"
            class="overflow-auto rounded-lg border"
            style="border-color: var(--border-primary, #e5e7eb);">
            <iframe
              v-if="previewUrl"
              data-test="preview-frame"
              title="Page preview"
              :src="previewUrl"
              sandbox="allow-scripts allow-forms allow-popups"
              referrerpolicy="no-referrer"
              class="block mx-auto"
              :style="frameStyle" />
            <p v-else class="p-6 text-sm text-center" style="color: var(--text-secondary);">
              {{ previewState === 'failed' ? 'The preview could not be prepared. Try again.' : 'Save your draft, then choose Preview to see it as visitors will.' }}
            </p>
          </div>
          <p class="text-xs" style="color: var(--text-secondary);">
            Previews are private, expire after 15 minutes and never send real leads.
          </p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import SectionEditor from './SectionEditor.vue'
import { blankValue, trimSectionFields } from './section_values'

const props = defineProps({
  page: { type: Object, required: true },
  saveDraft: { type: Function, required: true },
  changePath: { type: Function, default: null },
  requestPreview: { type: Function, default: null },
  uploadAsset: { type: Function, default: null },
  listAssets: { type: Function, default: null },
  readOnly: { type: Boolean, default: false }
})

const emit = defineEmits(['saved', 'reload', 'publish'])

const SECTION_TITLES = {
  hero: 'Top of page',
  form: 'Contact form',
  contact: 'Contact details',
  services: 'Services',
  benefits: 'Benefits',
  process: 'How it works',
  service_area: 'Service area',
  faq: 'Questions and answers',
  trust: 'Confirmed information',
  offer: 'Offer',
  cta: 'Call to action',
  credentials: 'Credentials',
  locations: 'Locations',
  tiers: 'Options and prices',
  testimonials: 'Customer reviews',
  logo_bar: 'Logos'
}
const BRAND_COLORS = [
  { key: 'primary_color', label: 'Main colour', pick: 'Pick main colour', fallback: '#000000', placeholder: '#1f3a5f' },
  { key: 'accent_color', label: 'Accent colour', pick: 'Pick accent colour', fallback: '#000000', placeholder: '#e8a33d' },
  { key: 'paper_color',
    label: 'Page background colour',
    pick: 'Pick page background colour',
    fallback: '#ffffff',
    placeholder: '#ffffff',
    hint: 'Leave empty to use the template default.' },
  { key: 'ink_color',
    label: 'Text colour',
    pick: 'Pick text colour',
    fallback: '#000000',
    placeholder: '#1a1a1a',
    hint: 'Leave empty to use the template default.' }
]
// Optional colours: an emptied value is removed so the template default applies.
const OPTIONAL_COLORS = ['paper_color', 'ink_color']
const FIXED_FIRST = 'hero'
const FIXED_LAST = 'contact'
const FIELD_TYPES = ['text', 'email', 'phone', 'textarea', 'checkbox']
const FIELD_TYPE_LABELS = { text: 'Short text', email: 'Email', phone: 'Phone', textarea: 'Long text', checkbox: 'Checkbox' }
const COLOR = /^#[0-9A-Fa-f]{6}$/

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

const document = reactive(clone(props.page.document))
const title = ref(props.page.title)
const version = ref(props.page.version)
const draftVersion = ref(props.page.draft_version)
const baseline = ref(JSON.stringify({ document: props.page.document, title: props.page.title }))
const saving = ref(false)
const conflict = ref(null)
const errors = ref({})
const message = ref(null)
const newSectionType = ref('')
const viewport = ref('desktop')
const previewUrl = ref(null)
const previewState = ref('idle')
const editingPath = ref(false)
const pathDraft = ref('')
const pathSaving = ref(false)
const assets = ref([])
const assetsState = ref('idle')
const logoUploading = ref(false)

const readOnly = computed(() => props.readOnly || props.page.archived === true || props.page.status === 'archived')
const readOnlyReason = computed(() => (props.page.archived || props.page.status === 'archived' ?
  'This page is archived and read-only. Its history and leads are kept.' :
  'You can view this page but not change it.'))
const dirty = computed(() => JSON.stringify({ document, title: title.value }) !== baseline.value)
const template = computed(() => props.page.template || {})
const templateSections = computed(() => template.value.sections || [])
const statusLabel = computed(() => ({ draft: 'Draft (not published)', published: 'Published', archived: 'Archived' }[props.page.status] ||
  props.page.status))
const messageClass = computed(() => {
  if (!message.value) return ''
  return message.value.kind === 'success' ? 'text-green-700' : 'text-red-700'
})
const addableTypes = computed(() => {
  const present = document.sections.map((section) => section.type)
  return templateSections.value
    .filter((section) => section.allowed_operations?.add && !present.includes(section.type))
    .map((section) => section.type)
})
const frameStyle = computed(() => (viewport.value === 'mobile' ?
  'width: 390px; max-width: 100%; height: 720px; border: 0;' :
  'width: 100%; height: 720px; border: 0;'))

watch(addableTypes, (types) => {
  if (!types.includes(newSectionType.value)) [newSectionType.value = ''] = types
}, { immediate: true })

// A newer page from the parent replaces the draft only when nothing is
// unsaved; unsaved edits are never discarded silently.
watch(() => props.page, (page) => {
  if (dirty.value || !page) return
  reset(page)
})

function reset(page) {
  const next = clone(page.document)
  Object.keys(document).forEach((key) => { delete document[key] })
  Object.assign(document, next)
  title.value = page.title
  version.value = page.version
  draftVersion.value = page.draft_version
  baseline.value = JSON.stringify({ document: page.document, title: page.title })
  conflict.value = null
  errors.value = {}
}

function definitionFor(type) {
  return templateSections.value.find((section) => section.type === type) || null
}

function editorFor(type) {
  return template.value.editor?.[type] || null
}

function canMove(index, offset) {
  if (readOnly.value) return false
  const section = document.sections[index]
  const target = document.sections[index + offset]
  if (!target || [FIXED_FIRST, FIXED_LAST].includes(section.type) || [FIXED_FIRST, FIXED_LAST].includes(target.type)) return false
  const definition = definitionFor(section.type)
  return definition ? definition.allowed_operations?.reorder !== false : true
}

function canRemove(section) {
  if (readOnly.value || [FIXED_FIRST, FIXED_LAST, 'form'].includes(section.type)) return false
  const definition = definitionFor(section.type)
  return definition ? definition.allowed_operations?.remove === true : false
}

function updateSection(index, section) {
  document.sections.splice(index, 1, section)
}

function moveSection(index, offset) {
  if (!canMove(index, offset)) return
  const [moved] = document.sections.splice(index, 1)
  document.sections.splice(index + offset, 0, moved)
}

function removeSection(index) {
  document.sections.splice(index, 1)
}

function blankFields(definition, editor) {
  const properties = definition?.fields?.properties || {}
  const keys = editor?.fields || definition?.fields?.required || []
  return Object.fromEntries(keys.map((key) => {
    if (key === 'items') {
      const itemKeys = editor?.item_fields || Object.keys(properties.items?.items?.properties || {})
      const count = Math.max(editor?.min_items ?? properties.items?.minItems ?? 1, 1)
      const itemProperties = properties.items?.items?.properties || {}
      const blankItem = () => Object.fromEntries(itemKeys.map((item) => [item, blankValue(item, itemProperties[item])])
        .filter(([, value]) => value !== undefined))
      return [key, Array.from({ length: count }, blankItem)]
    }
    return [key, blankValue(key, properties[key])]
  }).filter(([, value]) => value !== undefined))
}

function addSection() {
  const type = newSectionType.value
  if (!type) return
  const definition = definitionFor(type)
  const section = { id: `${type}_${Date.now().toString(36)}`, type, schema_id: `${type}/v1`, fields: blankFields(definition, editorFor(type)) }
  const contactIndex = document.sections.findIndex((item) => item.type === FIXED_LAST)
  document.sections.splice(contactIndex === -1 ? document.sections.length : contactIndex, 0, section)
}

function setFormField(formIndex, fieldIndex, change) {
  const form = document.forms[formIndex]
  form.fields.splice(fieldIndex, 1, { ...form.fields[fieldIndex], ...change })
}

function addFormField(formIndex) {
  const form = document.forms[formIndex]
  const keys = form.fields.map((field) => field.key)
  let number = form.fields.length + 1
  while (keys.includes(`field_${number}`)) number += 1
  const consentIndex = form.fields.findIndex((field) => field.key === 'consent')
  const field = { key: `field_${number}`, label: 'New question', type: 'text', required: false }
  form.fields.splice(consentIndex === -1 ? form.fields.length : consentIndex, 0, field)
}

function removeFormField(formIndex, fieldIndex) {
  document.forms[formIndex].fields.splice(fieldIndex, 1)
}

// The consent statement and its required checkbox label are one fact.
function setConsent(formIndex, text) {
  const form = document.forms[formIndex]
  form.consent_text = text
  const index = form.fields.findIndex((field) => field.key === 'consent')
  if (index >= 0) {
    form.fields.splice(index, 1, { ...form.fields[index], label: text, type: 'checkbox', required: true })
  } else if (text) {
    form.fields.push({ key: 'consent', label: text, type: 'checkbox', required: true })
  }
}

function setBrand(change) {
  const next = { ...document.brand, ...change }
  OPTIONAL_COLORS.forEach((key) => {
    if (key in change && !change[key]) delete next[key]
  })
  document.brand = next
}

function assetLabel(asset) {
  return asset.width && asset.height ? `${asset.filename} (${asset.width} × ${asset.height})` : asset.filename
}

async function loadAssets() {
  if (!props.listAssets) return
  assetsState.value = 'loading'
  try {
    assets.value = (await props.listAssets()) || []
    assetsState.value = 'ready'
  } catch (error) {
    assetsState.value = 'failed'
  }
}

async function uploadLogo(event) {
  const file = event.target.files?.[0]
  if (!file || !props.uploadAsset) return
  logoUploading.value = true
  try {
    const assetId = await props.uploadAsset(file)
    setBrand({ logo_asset_id: assetId })
    await loadAssets()
  } catch (error) {
    showError(error)
  } finally {
    logoUploading.value = false
    // eslint-disable-next-line no-param-reassign
    event.target.value = ''
  }
}

onMounted(loadAssets)

function validColor(value) {
  return COLOR.test(value || '')
}

function sectionErrors(index) {
  const prefix = `/sections/${index}`
  const result = {}
  Object.entries(errors.value).forEach(([path, messages]) => {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      const rest = path.slice(prefix.length).replace(/^\/(fields\/)?/, '')
      result[rest] = [...(result[rest] || []), ...messages]
    }
  })
  return result
}

function errorsUnder(prefix) {
  return Object.entries(errors.value).filter(([path]) => path.startsWith(prefix)).flatMap(([, messages]) => messages)
}

function formErrors(index) {
  return errorsUnder(`/forms/${index}`)
}

// String lists lose their blank rows on save (an emptied optional list is removed).
function normalizeLists() {
  document.sections.forEach((section, index) => {
    const fields = trimSectionFields(section.fields)
    if (JSON.stringify(fields) !== JSON.stringify(section.fields)) document.sections.splice(index, 1, { ...section, fields })
  })
}

function payload() {
  return { document: clone(document), title: title.value }
}

function adopt(page, sent) {
  version.value = page.version
  draftVersion.value = page.draft_version
  baseline.value = JSON.stringify(sent)
  conflict.value = null
  errors.value = {}
}

function showError(error) {
  message.value = { kind: 'error', text: describe(error) }
}

function describe(error) {
  if (error?.code === 'outcome_unknown' || error?.status === 0) {
    return 'We couldn\'t confirm the save. Your edits are still here; save again.'
  }
  if (error?.code === 'field_invalid') return 'Some fields need attention. Your edits are still here.'
  if (error?.code === 'resource_not_found') return 'This page is no longer available to you.'
  return 'The draft could not be saved. Your edits are still here; try again.'
}

async function submit(expectedVersion) {
  if (saving.value) return
  saving.value = true
  message.value = null
  normalizeLists()
  const sent = payload()
  try {
    const result = await props.saveDraft(sent, expectedVersion)
    adopt(result.data, sent)
    message.value = { kind: 'success', text: 'Saved' }
    emit('saved', result.data)
  } catch (error) {
    if (error?.code === 'version_conflict') {
      conflict.value = { currentVersion: error.current_version ?? null }
      message.value = null
    } else {
      errors.value = error?.fields || {}
      showError(error)
    }
  } finally {
    saving.value = false
  }
}

function save() {
  return submit(version.value)
}

// Explicit choice after a conflict: the owner's edits replace the newer draft.
function overwrite() {
  if (!conflict.value?.currentVersion) {
    emit('reload')
    return undefined
  }
  return submit(conflict.value.currentVersion)
}

async function preview() {
  if (dirty.value) {
    message.value = { kind: 'error', text: 'Save your changes before previewing.' }
    return
  }
  previewState.value = 'loading'
  try {
    const result = await props.requestPreview(draftVersion.value)
    previewUrl.value = result?.url || null
    previewState.value = previewUrl.value ? 'ready' : 'failed'
  } catch (error) {
    previewState.value = 'failed'
    message.value = { kind: 'error',
      text: error?.code === 'version_conflict' ?
        'This page changed. Load the latest version before previewing.' :
        'The preview could not be prepared. Try again.' }
  }
}

function startPathEdit() {
  pathDraft.value = props.page.path
  editingPath.value = true
}

async function submitPath() {
  if (dirty.value) {
    message.value = { kind: 'error', text: 'Save your draft before changing the address.' }
    return
  }
  pathSaving.value = true
  try {
    const result = await props.changePath(pathDraft.value, version.value)
    document.route = { ...document.route, path: result.data.path }
    adopt(result.data, payload())
    editingPath.value = false
    message.value = { kind: 'success', text: `Address changed to ${result.data.path}` }
    emit('saved', result.data)
  } catch (error) {
    if (error?.code === 'version_conflict') {
      conflict.value = { currentVersion: error.current_version ?? null }
    } else if (error?.code === 'path_in_use') {
      message.value = { kind: 'error', text: 'Another page on this site already uses that address.' }
    } else if (error?.code === 'field_invalid') {
      message.value = { kind: 'error', text: 'Use lowercase letters, numbers and hyphens, starting with /.' }
    } else {
      showError(error)
    }
  } finally {
    pathSaving.value = false
  }
}

defineExpose({ dirty })
</script>
